<script setup>
import { ref, computed, watch } from 'vue'

import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotifications } from '@/composables/notifications'
import { useHighlightNewRow } from '@/composables/scroll'

import BottomTabsView from '@/components/shared/BottomTabsView.vue'
import NotificationListItem from '@/components/NotificationListItem.vue'

const ALL_TAB = 'all'
const UNREAD_TAB = 'unread'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { markAllAsReadAsync, openNotificationAsync, toggleReadAsync, snoozeAsync, useNotificationsDataSource } = useNotifications()

const listContainer = ref(null)
let listInstance = null

const isEmptyDataSource = ref(false)

webSiteStore.fabActions = []

const dataSource = useNotificationsDataSource({
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
})

const tabs = computed(() => [
  { name: UNREAD_TAB, label: `${i18n.t('common.unread')} (${webSiteStore.notificationsCount})` },
  { name: ALL_TAB, label: i18n.t('common.allFeminine') },
])

const activeTab = computed(() => (webSiteStore.notificationsUnreadOnly ? UNREAD_TAB : ALL_TAB))

const onTabChange = (tab) => {
  webSiteStore.notificationsUnreadOnly = tab === UNREAD_TAB
  dataSource.reload()
}

const reloadAndHighlightNewestAsync = async() => {
  const previousIds = new Set(dataSource.items().map(item => item.id))
  await dataSource.reload()
  const newest = dataSource.items()[0]
  if (!newest || previousIds.has(newest.id)) return
  await highlightRowAsync(newest.id, listContainer)
  listInstance?.repaint()
}

watch(() => webSiteStore.notificationsArrivedAt, reloadAndHighlightNewestAsync)

// A row's own change is repainted in place: a reload would drop the pages loaded below it.
let repaintedChange = false

watch(() => webSiteStore.notificationsChangedAt, () => {
  if (repaintedChange) {
    repaintedChange = false
    return
  }
  dataSource.reload()
})

const repaintReadState = ({ id, is_read }) => dataSource.store().push([{ type: 'update', key: id, data: { is_read } }])

const onNotificationToggleRead = async(notification) => {
  repaintedChange = true
  await toggleReadAsync(notification)
  repaintReadState(notification)
}

const onNotificationClick = async(notification) => {
  repaintedChange = !notification.is_read
  await openNotificationAsync(notification, { openDetail: true })
  repaintReadState(notification)
}

const onNotificationSnooze = (notification, until) => snoozeAsync([notification.id], until)
</script>

<template>
  <bottom-tabs-view
    :model-value="activeTab"
    :tabs="tabs"
    @update:model-value="onTabChange"
  >
    <div
      v-if="webSiteStore.notificationsCount > 0"
      class="row justify-end q-px-md q-pt-sm"
    >
      <q-btn
        flat
        dense
        no-caps
        color="primary"
        icon="mdi-check-all"
        :label="i18n.t('common.markAllAsRead')"
        @click="markAllAsReadAsync"
      />
    </div>
    <q-card
      flat
      class="col q-pa-none"
      style="min-height: 0;"
    >
      <q-card-section
        ref="listContainer"
        class="full-height q-pa-none"
      >
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="100%"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          repaint-changes-only
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: notification }">
            <notification-list-item
              :item="notification"
              :class="{ 'row-highlight': notification.id === highlightedRowId }"
              snoozable
              @click="onNotificationClick"
              @toggle-read="onNotificationToggleRead"
              @snooze="onNotificationSnooze"
            />
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="empty-container full-height"
        >
          <img
            src="/img/no-appointments.svg"
            :style="{ maxWidth: '80vw', objectFit: 'contain' }"
            class="q-mb-xl"
          >
          <p class="text-h5">
            {{ i18n.t(webSiteStore.notificationsUnreadOnly ? 'common.noUnreadNotification' : 'common.noNotification') }}
          </p>
          <p class="sub text-weight-light">
            {{ i18n.t('common.noNotificationHint') }}
          </p>
        </div>
      </q-card-section>
    </q-card>
  </bottom-tabs-view>
</template>
